import PropTypes from "prop-types"
import { Component } from "react"
import { Button, Card, Col, Container, Nav, Row } from "react-bootstrap"
import { Trans, withTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { HiLockClosed, HiOutlineEye, HiOutlineEyeOff, HiOutlineMenu, HiOutlineRefresh } from "react-icons/hi"
import { connect } from "react-redux"
import { SortableContainer, SortableElement } from "react-sortable-hoc"

import { updateDestination } from "../../../actions/destinations"
import { createGuide, deleteGuide } from "../../../actions/guides"
import ConfirmationModal from "../../../components/confirmation"
import IonIcon from "../../../components/ionIcon"
import VideoModal from "../../../components/video-modal"
import { USER_ROLES } from "../../../constants/userRoles"
import { twClassNames } from "../../../helpers/classNames"
import { getLocaleValue } from "../../../helpers/getLocaleValue"
import { mapOrder } from "../../../helpers/mapOrder"
import { reorderArray } from "../../../helpers/reorderArray"
import { GuideImage, GuideModal, GuideVideoThumbnail } from "./components"
import { GuideTextPreview } from "./components/text"

const isLocked = (guide, role) => {
  if (!guide) return true
  if (role === USER_ROLES.ADMIN || role === USER_ROLES.ACCOUNT_HOLDER) return false

  return guide.isLocked
}

const SortableItem = SortableElement(({ data, language, onClick, selected, role }) => (
  <Card
    body
    className={twClassNames("guide", selected && "selected", isLocked(data, role) && "locked")}
    onClick={() => onClick(data)}>
    <IonIcon name={data?.icon} />
    <span className="ml-3">{data && getLocaleValue(data, "title", language)}</span>
    <HiOutlineMenu className="float-right mt-1" />
    {data?.isDraft && <HiOutlineEyeOff className="float-right mt-1 mr-1" />}
    {data?.isLocked && <HiLockClosed className="float-right mt-1 mr-1" />}
  </Card>
))

const SortableList = SortableContainer(({ items, language, onClick, selectedGuideId, role }) => {
  return (
    <div>
      {items.map((data, index) => (
        <SortableItem
          key={`item-${index}`}
          index={index}
          data={data}
          language={language}
          onClick={onClick}
          role={role}
          selected={data?.id === selectedGuideId}
        />
      ))}
    </div>
  )
})

class GuidesContainer extends Component {
  constructor(props) {
    super(props)
    this.state = {
      language: props.i18n.resolvedLanguage ?? "en",
      selectedGuideId: null,
      showGuideModal: false,
      showConfirmationModal: false,
      videoModal: {
        display: false,
        video: null
      }
    }
  }

  showToast = (type, message, values) => {
    const { t } = this.props
    const translatedMessage = t("guides.notifications." + message, values)
    if (type === "success") {
      toast.success(translatedMessage)
    } else if (type === "error") {
      toast.error(translatedMessage)
    }
  }

  onSortEnd = ({ oldIndex, newIndex }) => {
    const { guides, order, id } = this.props.destination
    const orderedGuide = mapOrder(guides, order, "id")

    const updatedOrder = reorderArray(
      oldIndex,
      newIndex,
      orderedGuide.map(x => x.id)
    )

    if (id && oldIndex !== newIndex) {
      this.props
        .updateDestination(id, {
          order: updatedOrder
        })
        .catch(e => this.showToast("error", "sort.failed"))
    }
  }

  onSelectGuide = data => {
    this.setState({ selectedGuideId: data?.id })
  }

  onEdit = () => {
    const { selectedGuideId } = this.state
    if (selectedGuideId !== null) {
      this.props.history.push("/handbook/guides/" + selectedGuideId)
    }
  }

  onDelete = () => {
    const { selectedGuideId } = this.state
    const guide = this.props.destination?.guides.find(x => x.id === selectedGuideId)

    if (selectedGuideId && guide) {
      this.props
        .deleteGuide(selectedGuideId)
        .then(() => this.showToast("success", "deleteGuide.success", { topic: guide.topic }))
        .catch(e => this.showToast("error", "deleteGuide.failed"))
    }

    this.setState({ showConfirmationModal: false })
  }

  onCreate = values => {
    const { destination, createGuide, history } = this.props
    this.setState({ showGuideModal: false })

    if (destination) {
      const data = {
        title: values.titleEn ? values.titleEn : values.titleFi,
        titleEn: values.titleEn,
        titleFi: values.titleFi,
        icon: values.icon,
        isDraft: values.isDraft,
        content: {
          time: Date.now(),
          blocks: []
        },
        destination: destination.id,
        ownerTeamId: destination.ownerTeamId
      }

      createGuide(data)
        .then(response => history.push("/handbook/guides/" + response.payload.id))
        .catch(e => this.showToast("error", "createGuide.failed"))
    } else {
      this.showToast("error", "createGuide.failed")
    }
  }

  openGuideModal = () => {
    this.setState({ showGuideModal: true })
  }

  closeGuideModal = () => {
    this.setState({ showGuideModal: false })
  }

  openConfirmationModal = () => {
    this.setState({ showConfirmationModal: true })
  }

  closeConfirmationModal = () => {
    this.setState({ showConfirmationModal: false })
  }

  openVideoModal = data => {
    this.setState({ videoModal: { display: true, video: data } })
  }

  closeVideoModal = () => {
    this.setState({ videoModal: { display: false, video: null } })
  }

  render() {
    const { language, selectedGuideId, showGuideModal, showConfirmationModal, videoModal } = this.state
    const { t, destination, isLoading, user, teams } = this.props

    if (!destination || !user) {
      return null
    }

    const guide = destination.guides?.find(x => x.id === selectedGuideId) // Selected guide
    const guides = destination.guides ? mapOrder(destination.guides, destination.order, "id") : []
    const role = teams?.find(team => team.id === destination.ownerTeamId)?.userRole

    return (
      <Container fluid>
        <GuideModal show={showGuideModal} onSubmit={this.onCreate} onCancel={this.closeGuideModal} />
        <ConfirmationModal
          show={showConfirmationModal}
          title="delete"
          content="delete.guide"
          onOK={this.onDelete}
          onCancel={this.closeConfirmationModal}
          danger
        />
        <Row>
          <Col>
            <h3 className="mb-4 font-weight-bold">{t("guides.title")}</h3>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={8} className="guides-container">
            <Card className="mb-4">
              <Card.Header>
                <Row>
                  <Col xs={8}>
                    <h5 className="mb-0 mt-1">{destination && getLocaleValue(destination, "name", language)}</h5>
                  </Col>
                  <Col xs={4}>
                    <Nav
                      justify
                      defaultActiveKey={language}
                      onSelect={language => this.setState({ language })}
                      className="language-navigation">
                      <Nav.Item>
                        <Nav.Link eventKey="fi">{t("common.languages.fi")}</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="en">{t("common.languages.en")}</Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Header className="button-group">
                <Button
                  disabled={isLoading}
                  onClick={this.openGuideModal}
                  className="btn-default btn-block-xs-only mb-1 mb-sm-0"
                  variant="primary">
                  {t("guides.button.toolbar.new")}
                </Button>
                <Button
                  disabled={guide?.isLocked ?? true}
                  onClick={this.openConfirmationModal}
                  className="float-sm-right btn-default btn-block-xs-only mb-1 mb-sm-0"
                  variant="danger">
                  {t("guides.button.toolbar.delete")}
                </Button>
                <Button
                  disabled={isLocked(guide, role)}
                  onClick={this.onEdit}
                  className="float-sm-right btn-default btn-block-xs-only mr-sm-1 mb-1 mb-sm-0"
                  variant="primary">
                  {t("guides.button.toolbar.edit")}
                </Button>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col>
                    {isLoading && (
                      <div className="loading-spinner">
                        <HiOutlineRefresh className="slow-spin icon loading-spinner-icon" />
                      </div>
                    )}
                    <SortableList
                      role={role}
                      items={guides}
                      selectedGuideId={selectedGuideId}
                      language={language}
                      onSortEnd={this.onSortEnd}
                      distance={5}
                      onClick={this.onSelectGuide}
                      useWindowAsScrollContainer
                    />
                    {(!destination || !guides.length) && <p className="text-center mt-3">{t("guides.noContent")}</p>}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={4} className="preview-container">
            <Card className="mb-4">
              <Card.Header className="tw-flex tw-justify-between">
                <h5 className="mb-0">{t("guides.preview")}</h5>
                {guide && (
                  <div className="tw-group tw-relative">
                    <span
                      className={twClassNames(
                        "tw-mb-0 tw-place-self-center tw-text-sm",
                        guide.isDraft ? "tw-text-red-400" : "tw-text-dark-50"
                      )}>
                      {guide.isDraft ? (
                        <>
                          <HiOutlineEyeOff className="tw-mr-1" />
                          {t("guides.form.draft")}
                        </>
                      ) : (
                        <>
                          <HiOutlineEye className="tw-mr-1" />
                          {t("guides.form.publish")}
                        </>
                      )}
                    </span>
                    <span className="tw-absolute -tw-right-3 tw-top-8 tw-z-50 tw-hidden tw-w-[300px] tw-rounded-md tw-border tw-border-dark-15 tw-bg-white tw-p-3 tw-text-left tw-text-sm tw-opacity-0 tw-shadow-md tw-transition-opacity tw-duration-200 tw-ease-in group-hover:tw-block group-hover:tw-opacity-100">
                      {guide.isDraft ? (
                        <Trans i18nKey="guides.tooltips.isDraft.draft" />
                      ) : (
                        <Trans i18nKey="guides.tooltips.isDraft.publish" />
                      )}
                    </span>
                  </div>
                )}
              </Card.Header>
              <div className="mb-3">
                {guide &&
                  guide.content &&
                  guide.content.blocks &&
                  guide.content.blocks.map(({ data, type }, index) => {
                    switch (type) {
                      case "header":
                      case "paragraph":
                        return <GuideTextPreview key={index} data={data} language={language} type={type} />
                      case "image":
                        return <GuideImage key={index} data={data} preview />
                      case "video":
                        return (
                          <GuideVideoThumbnail key={index} data={data} preview list onClick={this.openVideoModal} />
                        )
                      default:
                        return null
                    }
                  })}
                {!guide && (
                  <p className="text-muted font-italic m-0 px-4 pt-3">{t("guides.selectedGuide.unselected")}</p>
                )}
                {guide?.content?.blocks && !guide.content.blocks.length && (
                  <p className="text-muted font-italic m-0 px-4 pt-3">{t("guides.selectedGuide.noContent")}</p>
                )}
              </div>
            </Card>
          </Col>
        </Row>
        <VideoModal video={videoModal.video} display={videoModal.display} close={this.closeVideoModal} />
      </Container>
    )
  }
}

GuidesContainer.propTypes = {
  isLoading: PropTypes.bool
}

GuidesContainer.defaultProps = {
  isLoading: false
}

const mapStateToProps = state => ({
  isLoading: state.destinations.isLoading,
  destination: state.destinations.destinations.find(x => x.id === state.destinations.selectedDestinationId),
  user: state.auth.user,
  teams: state.team.teams
})

const mapDispatchToProps = dispatch => ({
  updateDestination: (id, data) => dispatch(updateDestination(id, data)),
  deleteGuide: id => dispatch(deleteGuide(id)),
  createGuide: data => dispatch(createGuide(data))
})

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(GuidesContainer))
